<template>
  <v-dialog
    v-model="dialog"
    width="800"
    @click:outside="closeDialog"
    persistent
  >
    <v-card class="custom-card" rounded="0">
      <v-toolbar flat color="primary" class="white--text">
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.edit") }} {{ $t("memberDataSection.factory") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon class="white--text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="black--text">
        <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
          <v-row>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="buyingStationName">
                {{ $t("memberDataSection.factoryName") }}<span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="formData.buyingStationName"
                placeholder="Enter here"
                outlined
                dense
                :rules="buyingStationNameRules"
                class="custom-field"
                hide-details="auto"
              />
            </div>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="email">
                {{ $t("memberDataSection.primaryEmailAddress") }}<span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="formData.email"
                placeholder="Enter here"
                outlined
                dense
                :rules="emailRules"
                class="custom-field"
                hide-details="auto"
              />
            </div>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="buyingStationNumber">
                {{ $t("memberDataSection.factoryNumber") }}
              </label>
              <v-text-field
                v-model="formData.buyingStationNumber"
                placeholder="Enter here"
                outlined
                dense
                class="custom-field"
                hide-details="auto"
              />
            </div>

            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12 py-0 field-block">
              <label for="societyId">{{ $t("memberDataSection.society") }}</label>
              <v-autocomplete
                v-model="formData.societyId"
                :items="societies"
                item-text="label"
                item-value="id"
                :loading="societiesLoading"
                placeholder="Select society"
                outlined
                dense
                class="custom-field"
                hide-details="auto"
                clearable
              />
            </div>
          </v-row>

          <v-card-actions class="mt-10" style="padding: 0 60px 20px 60px !important;">
            <v-spacer />
            <v-btn
              @click="closeDialog"
              outlined
              right
              height="44"
              width="116"
              depressed
              color="primary"
            >
              {{ $t("cancel") }}
            </v-btn>
            <v-btn
              @click="submit"
              right
              height="44"
              width="116"
              color="primary"
              :disabled="!valid"
            >
              {{ $t("submit") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmUnlinkSocietyDialog" width="440" :retain-focus="false" persistent>
      <v-card class="custom-card" rounded="0">
        <v-card-title class="text-subtitle-1 font-weight-bold black--text">
          {{ $t("memberDataSection.factoryWithoutSocietyTitle") }}
        </v-card-title>
        <v-card-text class="black--text">
          {{ $t("memberDataSection.factoryUnlinkSocietyConfirm") }}
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn outlined height="40" min-width="100" depressed color="primary" @click="confirmUnlinkSocietyDialog = false">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn height="40" min-width="100" color="primary" @click="confirmUnlinkSociety">
            {{ $t("memberDataSection.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import BuyingStationService from "@/_services/BuyingStationService";
import UserService from "@/_services/UserService";
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
  name: "EditUser",
  mixins: [LoadingMixin],
  props: {
    dialogU: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      valid: false,
      societies: [],
      societiesLoading: false,
      originalSocietyId: null,
      confirmUnlinkSocietyDialog: false,
      formData: {
        buyingStationName: "",
        email: "",
        buyingStationNumber: "",
        societyId: null,
      },
    };
  },
  computed: {
    dialog() {
      return this.dialogU;
    },
    buyingStationNameRules() {
      return [(v) => !!v || "Buying station name is required"];
    },
    emailRules() {
      return [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ];
    },
  },
  created() {
    this.fetchSocieties();
  },
  watch: {
    dialogU: {
      handler(newVal) {
        if (newVal) {
          this.syncFromUser();
          this.fetchSocieties();
        }
      },
      immediate: true,
    },
    user: {
      handler() {
        if (this.dialogU) this.syncFromUser();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    syncFromUser() {
      const u = this.user || {};
      this.formData.buyingStationName =
        `${u.firstName || ""} ${u.middleName || ""}`.trim() || u.fullName || u.name || "";
      this.formData.email = u.email || "";
      this.formData.buyingStationNumber = u.id_number ?? u.buyingStationNumber ?? "";
      this.formData.societyId = u.societyId ?? (u.society && u.society.id) ?? null;
      this.originalSocietyId = this.formData.societyId;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    async fetchSocieties() {
      this.societiesLoading = true;
      try {
        const response = await UserService.getSocieties();
        const list = (response && response.data) || response || [];
        this.societies = list.map((sc) => ({
          id: sc.id,
          label: sc.fullName || `${sc.firstName || ""} ${sc.lastName || ""}`.trim() || sc.email,
        }));
      } catch (e) {
        this.societies = [];
      } finally {
        this.societiesLoading = false;
      }
    },
    closeDialog() {
      this.$emit("closeUpdateUser");
    },
    submit() {
      if (!this.$refs.form.validate()) return;

      // Only warn when an existing society link is being removed.
      if (this.originalSocietyId && !this.formData.societyId) {
        this.confirmUnlinkSocietyDialog = true;
        return;
      }

      this.updateFactory();
    },
    confirmUnlinkSociety() {
      this.confirmUnlinkSocietyDialog = false;
      this.updateFactory();
    },
    async updateFactory() {
      this.startLoading();
      try {
        const u = this.user || {};
        // Stations are updated via the dedicated buying-station endpoint (not the generic
        // app-user update), so no lastName / membershipTypeId is required.
        const payload = {
          name: this.formData.buyingStationName,
          stationId: this.formData.buyingStationNumber || "",
          email: this.formData.email || "",
        };
        if (this.formData.societyId) payload.societyId = this.formData.societyId;
        const res = await BuyingStationService.updateStation(u.id, payload);
        this.$emit("appUserUpdated", {
          success: res?.success ?? true,
          message: res?.message || "Updated successfully.",
          user: { ...u, ...payload },
        });
        this.closeDialog();
      } catch (err) {
        const message =
          err?.data?.message || err?.response?.data?.message || err?.message || "Failed to update factory.";
        this.$emit("appUserUpdated", { success: false, message });
      } finally {
        this.stopLoading();
      }
    },
  },
};
</script>

<style scoped>
.custom-card {
  background: #ffffff !important;
  box-shadow: 0px 30px 30px rgba(106, 119, 132, 0.41) !important;
  border-radius: 23px !important;
}

.custom-card .v-card__text {
  width: 100%;
  padding: 37px 60px !important;
}

.field-block {
  margin-bottom: 16px;
}

.custom-field :deep(.v-input__details) {
  display: none !important;
}

.custom-field :deep(.v-label) {
  font-size: 14px !important;
}

.custom-field :deep(.v-input__control) {
  min-height: 40px !important;
}

.custom-field :deep(.v-input__slot) {
  min-height: 40px !important;
  background-color: white !important;
}

.custom-field :deep(.v-text-field__slot) {
  min-height: 40px !important;
}

.custom-field :deep(.v-text-field__slot input) {
  font-size: 14px !important;
  color: #666 !important;
  padding: 0 12px !important;
  height: 100% !important;
}

.custom-field.v-text-field--outlined :deep(fieldset) {
  border-color: #e0e0e0 !important;
}

@media (max-width: 600px) {
  .custom-card .v-card__text {
    padding: 16px !important;
  }
}
</style>

